(() => {
  const root = document.querySelector('[data-kingdom-certificate-fields]');
  if (!root) return;

  const form = root.closest('form');
  const config = JSON.parse(root.querySelector('[data-certificate-config]').textContent);
  const nameInput = root.querySelector('[name="properties[Citizen name]"]');
  const dateInput = root.querySelector('[name="properties[Citizenship date]"]');
  const variantInput = form.querySelector('input[name="id"]');
  const preview = root.querySelector('[data-certificate-preview]');
  const printInput = root.querySelector('[data-certificate-print]');
  const feedback = root.querySelector('[data-certificate-error]');
  const images = new Map();
  let busy = false;
  let renderCount = 0;

  const layouts = {
    'KOG-CERT-10A': { name: { x: 1632, y: 1870, width: 2260, size: 210, color: '#51221e' }, date: { x: 520, y: 3400, width: 840, size: 76, color: '#35231d' } },
    'KOG-CERT-12C': { name: { x: 1632, y: 1870, width: 2110, size: 216, color: '#202d20' }, date: { x: 390, y: 3690, width: 1150, size: 76, color: '#202d20' } },
    'KOG-CERT-13B': { name: { x: 1632, y: 2540, width: 2310, size: 193, color: '#202840' }, date: { x: 335, y: 3570, width: 930, size: 76, color: '#202840' } }
  };

  function selectedSku() {
    return config.variants.find(item => item.id === variantInput.value)?.sku || 'KOG-CERT-10A';
  }

  function master(sku) {
    if (!images.has(sku)) {
      images.set(sku, new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('The certificate artwork did not load.'));
        image.src = config.masters[sku];
      }));
    }
    return images.get(sku);
  }

  function dateLabel(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match || value < '2011-04-28') return '';
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    if (date.getUTCFullYear() !== Number(match[1]) || date.getUTCMonth() !== Number(match[2]) - 1 || date.getUTCDate() !== Number(match[3])) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date).toUpperCase();
  }

  function fitText(ctx, text, options, centered) {
    let fontSize = options.size;
    ctx.fillStyle = options.color;
    ctx.textBaseline = 'top';
    ctx.textAlign = centered ? 'center' : 'left';
    while (fontSize > 65) {
      ctx.font = `bold ${fontSize}px Georgia, serif`;
      if (ctx.measureText(text).width <= options.width) break;
      fontSize -= 4;
    }
    ctx.fillText(text, options.x, options.y, options.width);
  }

  async function draw(canvas, sku) {
    const layout = layouts[sku];
    if (!layout) throw new Error('Choose a certificate design.');
    const image = await master(sku);
    const ctx = canvas.getContext('2d');
    const scale = canvas.width / 3264;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(scale, scale);
    const name = nameInput.value.trim();
    if (name) fitText(ctx, name, layout.name, true);
    const date = dateLabel(dateInput.value);
    if (date) fitText(ctx, date, layout.date, false);
    ctx.restore();
  }

  async function renderPreview() {
    const thisRender = ++renderCount;
    const sku = selectedSku();
    try {
      await document.fonts.ready;
      if (thisRender !== renderCount) return;
      await draw(preview, sku);
      if (thisRender === renderCount) feedback.textContent = '';
    } catch (error) {
      if (thisRender === renderCount) feedback.textContent = error.message || 'The preview could not be prepared.';
    }
  }

  function pdfFromJpeg(jpeg) {
    const encoder = new TextEncoder();
    const chunks = [];
    const offsets = [0];
    let length = 0;
    const append = bytes => { chunks.push(bytes); length += bytes.length; };
    const text = value => append(encoder.encode(value));
    const object = (number, body) => { offsets[number] = length; text(`${number} 0 obj\n${body}\nendobj\n`); };
    text('%PDF-1.4\n');
    object(1, '<< /Type /Catalog /Pages 2 0 R >>');
    object(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    object(3, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /XObject << /Art 4 0 R >> >> /Contents 5 0 R >>');
    offsets[4] = length;
    text(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width 3264 /Height 4224 /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`);
    append(jpeg);
    text('\nendstream\nendobj\n');
    const drawCommand = 'q 612 0 0 792 0 0 cm /Art Do Q\n';
    object(5, `<< /Length ${encoder.encode(drawCommand).length} >>\nstream\n${drawCommand}endstream`);
    const xref = length;
    text('xref\n0 6\n0000000000 65535 f \n');
    for (let number = 1; number <= 5; number += 1) text(`${String(offsets[number]).padStart(10, '0')} 00000 n \n`);
    text(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);
    return new Blob(chunks, { type: 'application/pdf' });
  }

  async function generatePdf(sku) {
    const canvas = document.createElement('canvas');
    canvas.width = 3264;
    canvas.height = 4224;
    await draw(canvas, sku);
    const jpeg = await new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not export certificate artwork.')), 'image/jpeg', 0.98));
    return pdfFromJpeg(new Uint8Array(await jpeg.arrayBuffer()));
  }

  nameInput.addEventListener('input', renderPreview);
  dateInput.addEventListener('input', renderPreview);
  document.addEventListener('change', () => setTimeout(renderPreview, 0));
  form.addEventListener('submit', async event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    if (!form.reportValidity()) return;
    const sku = selectedSku();
    if (!layouts[sku] || !dateLabel(dateInput.value)) {
      feedback.textContent = 'Choose a certificate design and a citizenship date on or after April 28, 2011.';
      feedback.focus();
      return;
    }
    busy = true;
    feedback.textContent = 'Preparing your certificate…';
    try {
      const pdf = await generatePdf(sku);
      const transfer = new DataTransfer();
      transfer.items.add(new File([pdf], `${sku}-certificate.pdf`, { type: 'application/pdf' }));
      printInput.files = transfer.files;
      HTMLFormElement.prototype.submit.call(form);
    } catch (error) {
      feedback.textContent = error.message || 'The certificate could not be prepared. Please try again.';
      feedback.focus();
      busy = false;
    }
  }, true);
  renderPreview();
})();
